/* eslint-disable default-case */
import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "../utils/firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { getDocs, getDoc, collection, doc } from "firebase/firestore";
import { FileUploaderDropzone } from "../Components/FileUploader";
import "./grantprofile.css";
import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField, Box, Modal } from "@mui/material";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import { generateFirestoreAutoId } from "../Contexts/utilFunctions";
import { useDispatch } from "react-redux";
import NavBar from "../Components/NavBar";
import { addClient, updateProposalInfo } from "../Redux/slices/clientSlice";
import cityTimezones from "city-timezones";
import CityTimeZoneSelector from '../Components/CityTimeZoneSelector'
import IndustryMultiSelect from "../Components/IndustryMultiSelect";
// NEW: Added imports for eligibility agent logic and layout
import LinearProgress from "@mui/material/LinearProgress";
import { IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import ClearIcon from "@mui/icons-material/Clear";
import ChatInput from "../Components/ChatInput";
// Extracted shared Genie/Copilot panel UI. Parent state and API handlers remain here.
import GeninieChatPanel from "../Components/GeninieChatPanel";
// LOCAL ELIGIBILITY HISTORY: Persist only the variable agent result for later
// same-browser proposal visits. Deterministic Genie guidance is reconstructed.
import {
  createEligibilityChatSnapshot,
  saveEligibilityChatSnapshot,
} from "../utils/eligibilityChatHistory";


const baseurl = process.env.REACT_APP_API_URL;
// FALLBACK: Use API URL from env or default to baseurl
const Eligibility_URL = process.env.REACT_APP_ELIGIBILITY_API_URL || "https://eligibility-197918893896.us-central1.run.app";
const ELIGIBILITY_API_BASE = Eligibility_URL.replace(/\/$/, "");

// CHAT CONTINUITY: Preserve the completed Check Eligibility guidance when the
// user enters this follow-on form, so Genie reads as one conversation.
const INITIAL_PROFILE_CHAT_MESSAGES = [
  "Please enter a Grant URL",
  "Please name your Proposal",
  "Optionally Upload or choose a previously uploaded file to improve results",
  "You can now start the Eligibility check!",
];

// CHAT STEP GUIDANCE: Each message is queued after its form step is selected or
// confirmed. The parent controls its two-second typing delay.
const PROFILE_STEP_MESSAGES = {
  1: "Please review and confirm your Company Info on the right, then click Confirm.",
  2: "Please review and confirm your Team Info on the right, then click Confirm.",
  3: "Please review and confirm the Grant Info on the right, then click Confirm.",
};

// Template proposals retain reusable applicant and team data. These values are
// grant-specific and must be cleared so a prior grant cannot leak into a new one.
const TEMPLATE_GRANT_FIELDS = new Set([
  "Grant_Proposal_Name",
  "Grant_Name",
  "Grant_Company_Name",
  "Grant_Amount",
  "Grant_Requirements",
  "Due_Date",
  "Due_Time",
  "Due_Time_Zone",
  "AM_PM",
  "Grant_Company_Website",
  "Grant_Application_Website",
  "Grant_Company_Mission_Statement",
  "Grant_Company_Vision_Statement",
  "Grant_Company_Target_Market",
  "Grant_Company_Category_Industry",
  "Agency_Type",
  "Geographic_Focus",
  "Grant_Company_LinkedIn",
  "Judges_Name",
  "Judges_Role",
  "Judges_Partnerships_Collaborations_1",
  "Judges_Partnerships_Collaborations_2",
  "Judges_LinkedIn",
  "Judges_More_Relevant_info",
]);

// GRANTDB ADAPTER: Keep the form's established field names stable while the
// database continues to use its source-specific labels.
const toFormText = (value, separator = ", ") => {
  if (Array.isArray(value)) return value.filter(Boolean).join(separator);
  return value == null ? "" : String(value);
};

const firstUrlIn = (...values) => {
  for (const value of values) {
    const match = toFormText(value).match(/https?:\/\/[^\s<>'"]+/i);
    if (match) return match[0].replace(/[),.;]+$/, "");
  }
  return "";
};

const grantDbAmount = (grant) => {
  const numericMaximum = Number(grant?.["Amount Max"]);
  if (Number.isFinite(numericMaximum) && numericMaximum > 0) {
    return numericMaximum;
  }

  const values = toFormText(grant?.["Estimated Size of Grant"]).match(/[\d,.]+/g) || [];
  const amounts = values
    .map((value) => Number(value.replace(/,/g, "")))
    .filter((value) => Number.isFinite(value));
  return amounts.length ? Math.max(...amounts) : null;
};

const mapGrantDbToFormData = (grant) => {
  if (!grant) return {};

  const grantName = toFormText(grant["Grant Name"] || grant["Funding Source"]);
  const applicantTypes = toFormText(grant["Applicant Types"]);
  const eligibilityCriteria = toFormText(grant["Additional Eligibility Criteria"]);

  return {
    Grant_Proposal_Name: grantName,
    Grant_Name: grantName,
    Grant_Company_Name: toFormText(grant["Funding Source"]),
    Grant_Amount: grantDbAmount(grant),
    Grant_Requirements: [
      applicantTypes && `Who May Apply: ${applicantTypes}`,
      eligibilityCriteria && `Eligibility: ${eligibilityCriteria}`,
    ].filter(Boolean).join("\n"),
    Due_Date: grant["Deadline Date"] || null,
    Grant_Company_Website: firstUrlIn(
      grant["Contact Information"],
      grant["Grant Description"]
    ),
    Grant_Application_Website: firstUrlIn(
      grant["Application URL"],
      grant["Pre-Application Information"],
      grant["URL for Full Text"],
      grant["Contact Information"]
    ),
    Grant_Company_Target_Market: applicantTypes,
    Grant_Company_Category_Industry: toFormText(
      grant["Categories List"] || grant.Categories
    ),
    Agency_Type: toFormText(grant.Agency),
    Geographic_Focus: toFormText(grant["Geographic Focus"]),
  };
};

function Main() {
  const dispatch = useDispatch();
  const location = useLocation();
  // Manual fallback shown only when extraction completes without questions.
  const [isApplicationQuestionsOpen, setIsApplicationQuestionsOpen] = useState(false);
  const [applicationQuestions, setApplicationQuestions] = useState([""]);
  const [applicationQuestionUndo, setApplicationQuestionUndo] = useState({});
  const [applicationQuestionsError, setApplicationQuestionsError] = useState("");
  const [submittedManualApplicationQuestions, setSubmittedManualApplicationQuestions] = useState([]);
  const proposalNameFromNavigation =
    typeof location.state?.Grant_Proposal_Name === "string"
      ? location.state.Grant_Proposal_Name.trim()
      : "";
  const [profileName, setProfileName] = useState(proposalNameFromNavigation);

  const [grantProposalForm_id, setGrantProposalForm_id] = useState("");
  const [client_Organization_id, setClient_Organization_id] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [childData, setChildData] = useState(null);
  const [setEdited, setIsEdited] = useState(false);
  
  const [formData, setFormData] = useState({
    Grant_Proposal_Name: proposalNameFromNavigation,

    User_name: "",
    Company_Name: "",
    User_role: "",
    Company_Website: "",
    Mission_Statement: "",
    Vision_Statement: "",
    Target_Users_Market: "",
    Focused_Industry: [],
    Problem_Statement: "",
    Solution_Statement: "",
    Programs_Services: "",
    Track_record: "",
    Current_funding: "",
    Budget: "",
    Stakeholders: "",
    Product_Features: "",
    Goals_Objectives_Outcomes: "",
    Long_Term_Plan: "",
    Partners_Collaborators_1: "",
    Partners_Collaborators_2: "",

    Team_Member_1: "",
    Team_Member_Role_1: "",
    Team_Member_Education_1: "",
    Team_Member_1_LinkedIn: "",
    Team_Member_Interests_Hobbies_1: "",
    Team_Member_Super_Power_Skills_1: "",

    Team_Member_2: "",
    Team_Member_Role_2: "",
    Team_Member_Education_2: "",
    Team_Member_2_LinkedIn: "",
    Team_Member_Interests_Hobbies_2: "",
    Team_Member_Super_Power_Skills_2: "",

    Team_Member_3: "",
    Team_Member_Role_3: "",
    Team_Member_Education_3: "",
    Team_Member_3_LinkedIn: "",
    Team_Member_Interests_Hobbies_3: "",
    Team_Member_Super_Power_Skills_3: "",

    Team_Member_4: "",
    Team_Member_Role_4: "",
    Team_Member_Education_4: "",
    Team_Member_4_LinkedIn: "",
    Team_Member_Interests_Hobbies_4: "",
    Team_Member_Super_Power_Skills_4: "",

    Grant_Name: "",
    Grant_Company_Name: "",
    Grant_Amount: null,
    Grant_Requirements: "",
    Due_Date: null,
    Due_Time: null,
    Due_Time_Zone: "",
    AM_PM: "",
    Grant_Company_Website: "",
    Grant_Application_Website: "",
    Grant_Company_Mission_Statement: "",
    Grant_Company_Vision_Statement: "",
    Grant_Company_Target_Market: "",
    Grant_Company_Category_Industry: "",
    Agency_Type: "",
    Geographic_Focus: "",
    Grant_Company_LinkedIn: "",

    Judges_Name: "",
    Judges_Role: "",
    Judges_Partnerships_Collaborations_1: "",
    Judges_Partnerships_Collaborations_2: "",
    Judges_LinkedIn: "",
    Judges_More_Relevant_info: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overwriteConfirmed, setOverwriteConfirmed] = useState(false);
  

  // NEW: Eligibility Agent State (Phase 3 Integration)
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState(null);
  const [proposalPromptAnswer, setProposalPromptAnswer] = useState(null);
  const [activeChatTab, setActiveChatTab] = useState("eligibility"); // 'copilot' or 'content'
  const [copilotInput, setCopilotInput] = useState("");
  const [hasConfirmedGrantInfo, setHasConfirmedGrantInfo] = useState(false);
    const [clarifications, setClarifications] = useState({});

  // Only the manual Check Eligibility page has these setup messages to carry
  // forward. GrantDB starts directly with the form-specific guidance.
  const [chatMessages, setChatMessages] = useState(() =>
    location.state?.grantDbGrant ? [] : INITIAL_PROFILE_CHAT_MESSAGES
  );
  const [confirmedChatSteps, setConfirmedChatSteps] = useState([]);
  const [pendingChatSteps, setPendingChatSteps] = useState([1]);
  const [isProfileChatTyping, setIsProfileChatTyping] = useState(true);
  const displayedChatStepsRef = useRef(new Set());

  const navigate = useNavigate();
  const user = auth.currentUser;
  const uid = user ? user.uid : null;
  const totalSteps = 4;

  const addApplicationQuestion = () => {
    setApplicationQuestions((questions) => [...questions, ""]);
  };

  const updateApplicationQuestion = (index, value) => {
    setApplicationQuestions((questions) =>
      questions.map((question, questionIndex) =>
        questionIndex === index ? value : question
      )
    );
  };

  const clearApplicationQuestion = (index) => {
    const questionToClear = applicationQuestions[index];
    if (!questionToClear) return;

    setApplicationQuestionUndo((undoValues) => ({
      ...undoValues,
      [index]: questionToClear,
    }));
    setApplicationQuestions((questions) =>
      questions.map((question, questionIndex) =>
        questionIndex === index ? "" : question
      )
    );
  };

  const undoClearApplicationQuestion = (index) => {
    const previousQuestion = applicationQuestionUndo[index];
    if (previousQuestion === undefined) return;

    setApplicationQuestions((questions) =>
      questions.map((question, questionIndex) =>
        questionIndex === index ? previousQuestion : question
      )
    );
    setApplicationQuestionUndo((undoValues) => {
      const nextUndoValues = { ...undoValues };
      delete nextUndoValues[index];
      return nextUndoValues;
    });
  };

  const deleteApplicationQuestion = (index) => {
    if (applicationQuestions.length === 1) return;

    setApplicationQuestions((questions) =>
      questions.filter((_, questionIndex) => questionIndex !== index)
    );
    setApplicationQuestionUndo((undoValues) =>
      Object.entries(undoValues).reduce((nextUndoValues, [key, value]) => {
        const questionIndex = Number(key);
        if (questionIndex < index) nextUndoValues[questionIndex] = value;
        if (questionIndex > index) nextUndoValues[questionIndex - 1] = value;
        return nextUndoValues;
      }, {})
    );
  };

  const closeApplicationQuestions = () => {
    setIsApplicationQuestionsOpen(false);
    setApplicationQuestionsError("");
  };

  const submitApplicationQuestions = () => {
    const questions = applicationQuestions
      .map((question) => question.trim())
      .filter(Boolean);

    if (!questions.length) {
      setApplicationQuestionsError("Please enter at least one application question.");
      return;
    }

    // Keep the fallback in page memory only. It is forwarded to Scout when
    // the user chooses to start the proposal and is never persisted here.
    setSubmittedManualApplicationQuestions(questions);
    closeApplicationQuestions();
  };

  // CHAT DELAY: Render queued instructions one at a time after Genie has typed
  // for two seconds. Cleanup prevents a delayed update after unmounting.
  useEffect(() => {
    const nextStep = pendingChatSteps[0];
    const nextMessage = PROFILE_STEP_MESSAGES[nextStep];

    if (!nextMessage) {
      setIsProfileChatTyping(false);
      return undefined;
    }

    setIsProfileChatTyping(true);
    const timerId = window.setTimeout(() => {
      setChatMessages((previousMessages) => [...previousMessages, nextMessage]);
      displayedChatStepsRef.current.add(nextStep);
      setPendingChatSteps((previousSteps) => previousSteps.slice(1));
    }, 3000);

    return () => window.clearTimeout(timerId);
  }, [pendingChatSteps]);

  // CHAT DELAY: Avoid duplicate instructions while allowing users to navigate
  // directly to a tab and still receive its contextual guidance.
  const queueChatStepMessage = (step) => {
    if (!PROFILE_STEP_MESSAGES[step] || displayedChatStepsRef.current.has(step)) {
      return;
    }

    setPendingChatSteps((previousSteps) =>
      previousSteps.includes(step) ? previousSteps : [...previousSteps, step]
    );
  };

//    useEffect(() => {     commented out, no longer using random grant ID but from Eligibility agent
//     console.log("Hello world")
//   setGrantProposalForm_id(generateFirestoreAutoId());
//   setClient_Organization_id(generateFirestoreAutoId());
//  }, []);
// useEffect(() => { //generate client organization ID if not exsist
//   if (!client_Organization_id) {
//     setClient_Organization_id(generateFirestoreAutoId());
//   }
// }, [client_Organization_id]);
useEffect(() => { 
  // Get existing client ID if it was passed in
  const existingClientId = location?.state?.currentClient?.client_id;
  
  if (existingClientId) {
    setClient_Organization_id(existingClientId);
  } else if (!client_Organization_id) {
    setClient_Organization_id(generateFirestoreAutoId());
  }
}, [client_Organization_id, location.state]);

useEffect(() => {
  // Eligibility supplies its own grant ID. Other proposal flows need one before files upload.
  if (!location.state?.grantUrl && !grantProposalForm_id) {
    setGrantProposalForm_id(generateFirestoreAutoId());
  }
}, [grantProposalForm_id, location.state?.grantUrl]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      const message =
        "You have unsaved changes. Are you sure you want to leave?";
      e.returnValue = message; // Required for Chrome
      return message; // Required for Firefox
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [id]: value }));
    setIsEdited(true);
  };

  // NEW: Eligibility Agent API Interaction
  const callAgent = async (url, body) => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `Server error: ${response.status}`);
    }
    return response.json();
  };

    const handleEligibilityCheck = async (clientOverride = null, cachedData = null) => {
    const grantUrl = location.state?.grantUrl;
    const grantDbGrant = location.state?.grantDbGrant;
    if (!grantUrl && !grantDbGrant) return;

    setEligibilityLoading(true);
    setEligibilityError(null);
    setProposalPromptAnswer(null);

    try {
      // The first eligibility request can start before the template hydration
      // effect completes. Fall back to route-state template/client values so the
      // agent still receives the reusable organization profile.
      const templateProposal = location.state?.currentProposal;
      const navigationClient = location.state?.currentClient;
      const client = clientOverride || {
        ...navigationClient,
        organization_name:
          formData.Company_Name ||
          templateProposal?.Company_Name ||
          navigationClient?.organization_name ||
          "New Company",
        website:
          formData.Company_Website ||
          templateProposal?.Company_Website ||
          navigationClient?.website ||
          "",
        mission:
          formData.Mission_Statement ||
          templateProposal?.Mission_Statement ||
          navigationClient?.mission_statement ||
          navigationClient?.mission ||
          "",
        vision:
          formData.Vision_Statement ||
          templateProposal?.Vision_Statement ||
          navigationClient?.vision_statement ||
          navigationClient?.vision ||
          "",
        problem_statement:
          formData.Problem_Statement || templateProposal?.Problem_Statement || "",
        solution_statement:
          formData.Solution_Statement || templateProposal?.Solution_Statement || "",
        clarifications,
        uid,
      };

      const isGrantDbCheck = Boolean(grantDbGrant);
      const body = isGrantDbCheck
        ? { grant_data: grantDbGrant, client_profile: client }
        : { grant_url: grantUrl, client_profile: client };
      if (cachedData && !isGrantDbCheck) {
        body.cached_content = cachedData.content;
        body.source_pages = cachedData.sourcePages;
      }

      const endpoint = isGrantDbCheck
        ? "/eligibility/check-from-data"
        : "/eligibility/check";
      const data = await callAgent(`${ELIGIBILITY_API_BASE}${endpoint}`, body);
      setEligibilityResult(data);
      if (data.grant_id) {
        setGrantProposalForm_id(data.grant_id);
      }

      // PRE-POPULATE Grant Info tab from Agent Results
      setFormData(prev => {
        let updatedDate = prev.Due_Date;
        
        // Convert 'YYYY-MM-DD' from AI to JS Date if valid
        if (data.due_date && data.due_date !== "ongoing") {
          const parsedDate = dayjs(data.due_date);
          if (parsedDate.isValid()) {
            updatedDate = parsedDate.toDate();
          }
        }

      return {
          ...prev,
          Grant_Name: data.grant_name || prev.Grant_Name,
          Grant_Company_Name: data.grant_company_name || prev.Grant_Company_Name,
          Grant_Amount: data.grant_amount || prev.Grant_Amount,
          Grant_Company_Website: grantUrl || prev.Grant_Company_Website,
          Due_Date: updatedDate,
          Due_Time: data.due_time || prev.Due_Time,
          Due_Time_Zone: data.due_time_zone || prev.Due_Time_Zone
        };
      });

    } catch (err) {
      console.error("Error checking eligibility:", err);
      setEligibilityError(err.message || "Failed to check eligibility.");
    } finally {
      setEligibilityLoading(false);
    }
  };

    const handleProposalPromptAction = async (action) => {
    setProposalPromptAnswer(action);
    const clientID = location?.state?.currentClient?.client_id || client_Organization_id;
    const body = {
      action: action,
      grant_id: eligibilityResult?.grant_id,
      client_profile: { organization_name: formData.Company_Name,
        uid: uid,
        client_id: clientID
       },
    };
    if (action === "yes" && submittedManualApplicationQuestions.length) {
      body.manual_application_questions = submittedManualApplicationQuestions;
    }
    try {
      await callAgent(`${ELIGIBILITY_API_BASE}/user-action`, body);
    } catch (e) {
      console.error("Error recording user action:", e);
    }
  };

    const handleAnswerUncertainty = async (question, answer) => {
    const answerText = answer ? "Yes" : "No";
    
    // Remember all previous clarifications plus the new one
    const newClarifications = {
      ...clarifications,
      [question]: answerText
    };
    setClarifications(newClarifications);









        const updatedClient = {
      organization_name: formData.Company_Name || "New Company",
      website: formData.Company_Website,
      mission: formData.Mission_Statement,
      vision: formData.Vision_Statement,
      problem_statement: formData.Problem_Statement,
      solution_statement: formData.Solution_Statement,
      clarifications: newClarifications,
      uid: uid
    };

    await handleEligibilityCheck(updatedClient, {
      content: eligibilityResult.cached_content,
      sourcePages: eligibilityResult.source_pages
    });
  };

  // Run the matching eligibility path for either a manually entered URL or a
  // complete GrantDB record.
  useEffect(() => {
    if (location.state?.grantUrl || location.state?.grantDbGrant) {
      handleEligibilityCheck();
    }
  }, []);

  // The Eligibility response arrives before its background first responders
  // finish. Poll the persisted application-question status and prompt for
  // manual entry only when that agent explicitly completes with "not_found".
  useEffect(() => {
    const grantId = eligibilityResult?.grant_id;
    if (!grantId) return undefined;

    let cancelled = false;
    let pollTimer;
    let attempts = 0;
    const maxAttempts = 200;

    const pollApplicationQuestionStatus = async () => {
      attempts += 1;
      try {
        const response = await fetch(`${baseurl}/api/get-grant-content/${grantId}`);
        if (response.ok) {
          const result = await response.json();
          if (cancelled) return;

          const hasStoredQuestions = Array.isArray(result.content) &&
            result.content.some((question) => String(question).trim());
          const storedBlankResult = result.has_application_questions &&
            typeof result.content === "string" &&
            (!result.content.trim() || result.content.trim().toUpperCase() === "N/A");
          const effectiveStatus = result.status ||
            (hasStoredQuestions ? "success" : storedBlankResult ? "not_found" : null);

          if (effectiveStatus === "not_found") {
            setApplicationQuestions([""]);
            setApplicationQuestionUndo({});
            setApplicationQuestionsError("");
            setIsApplicationQuestionsOpen(true);
            return;
          }

          // Both a successful extraction and an explicit agent error are final.
          // Errors must not be presented to the user as "no questions found".
          if (effectiveStatus === "success" || effectiveStatus === "error") return;
        }
      } catch (error) {
        console.error("Error checking application-question status:", error);
      }

      if (!cancelled && attempts < maxAttempts) {
        pollTimer = window.setTimeout(pollApplicationQuestionStatus, 3000);
      }
    };

    pollApplicationQuestionStatus();
    return () => {
      cancelled = true;
      window.clearTimeout(pollTimer);
    };
  }, [eligibilityResult?.grant_id]);

  useEffect(() => {
  console.log("Updated Timezone:", formData.Due_Time_Zone);
 }, [formData.Due_Time_Zone]);

  const handleDateChange = (data) => {
    if (data && data.isValid && data.isValid()) {
      // Use data.toDate() if it is a valid dayjs object
      setFormData({ ...formData, Due_Date: data.toDate() });
    } else {
      console.error("Invalid date object:", data);
      setFormData({ ...formData, Due_Date: null }); // Handle invalid dates
    }
    setIsEdited(true);
  };

  const addCollaborator = async (type) => {
    let user_id = localStorage.getItem("user_id");
    let data = {};

    if (type === "User_Collaborators") {
      data = {
        type: "User_Collaborator",
        user_id: user_id,
        collaborator_id: client_Organization_id,
        userCollaborator_data: {
          name: "",
          email: "",
          phone: "",
          role: "",
          GrantProfileForms: [grantProposalForm_id],
        },
      };
    } else {
      data = {
        type: "Client_Organization",
        user_id: user_id,
        collaborator_id: client_Organization_id,
        collaborator_data: {
          organization_name: formData.Company_Name,
          contact_person: formData.User_name,
          email: "",
          phone: "",
          website: formData.Company_Website,
        },
      };
    }
    dispatch(
      addClient({
        client_id:
          data.collaborator_id || client_Organization_id,
        contact_person: "",
        organization_name: data.collaborator_data?.organization_name,
        email: "",
        phone: "",
        website: data.collaborator_data?.website,
      })
    );
    const correctId = data.collaborator_id || client_Organization_id;
    setClient_Organization_id(correctId);
    localStorage.setItem("LastClient", correctId);
    try {
      const response = await fetch(`${baseurl}/add-collaborator`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      console.log("Collaborator added successfully:", result);
      return result;
    } catch (error) {
      console.error("Error adding collaborator:", error);
    }
  };

    useEffect(() => {
      const currentProposal = location?.state?.currentProposal;
      const currentClient = location?.state?.currentClient;
      const grantDbFormData = mapGrantDbToFormData(location?.state?.grantDbGrant);
    
      // Distinguish between an actual proposal to copy and a blank proposal flow
      const isCopyingProposal = currentProposal && !currentProposal.message;

      if (isCopyingProposal) {
        // Copy reusable applicant/team information only. The new grant URL is
        // evaluated independently, so every grant-specific template value stays
        // at its empty default until the eligibility agent supplies new data.
        const reusableTemplateData = Object.fromEntries(
          Object.entries(currentProposal).filter(([key]) =>
            !TEMPLATE_GRANT_FIELDS.has(key) && key !== "id" && key !== "is_deleted"
          )
        );
        const proposalName =
          proposalNameFromNavigation ||
          `${currentProposal.Grant_Proposal_Name || "Untitled Proposal"} – Copy`;

        setFormData((prev) => ({
          ...prev,
          ...reusableTemplateData,
          // The name entered on Check Eligibility always belongs to this new grant.
          Grant_Proposal_Name: proposalName,
        }));
        setProfileName(proposalName);
      } else if (currentClient) {
        // Pre-populate client information for a Blank Proposal
        setFormData(prev => {
          const newData = {
            ...prev,
            ...grantDbFormData,
            Company_Name: currentClient.organization_name || prev.Company_Name,
            User_name: currentClient.contact_person || prev.User_name,
            Company_Website: currentClient.website || prev.Company_Website,
            Mission_Statement: currentClient.mission_statement || currentClient.mission || prev.Mission_Statement,
            Vision_Statement: currentClient.vision_statement || currentClient.vision || prev.Vision_Statement,
            Grant_Proposal_Name:
              proposalNameFromNavigation ||
              grantDbFormData.Grant_Proposal_Name ||
              prev.Grant_Proposal_Name,

            // Pre-populate Team Member 1 (The main user/contact person)

            Team_Member_1: currentClient.contact_person || prev.Team_Member_1,
          };

          // If the client has employees/team members saved, pre-populate slots 2, 3, and 4
          if (currentClient.employees && Array.isArray(currentClient.employees)) {
            // Employee 1 goes to Team Member 2
            if (currentClient.employees[0]) {
              newData.Team_Member_2 = currentClient.employees[0].fullName || newData.Team_Member_2;
              newData.Team_Member_Role_2 = currentClient.employees[0].role || newData.Team_Member_Role_2;
              newData.Team_Member_2_LinkedIn = currentClient.employees[0].linkedin || newData.Team_Member_2_LinkedIn;
              // Add education or skills if your employee object holds them:
              // newData.Team_Member_Education_2 = currentClient.employees[0].education || ...
            }
            // Employee 2 goes to Team Member 3
            if (currentClient.employees[1]) {
              newData.Team_Member_3 = currentClient.employees[1].fullName || newData.Team_Member_3;
              newData.Team_Member_Role_3 = currentClient.employees[1].role || newData.Team_Member_Role_3;
              newData.Team_Member_3_LinkedIn = currentClient.employees[1].linkedin || newData.Team_Member_3_LinkedIn;
            }
            // Employee 3 goes to Team Member 4
            if (currentClient.employees[2]) {
              newData.Team_Member_4 = currentClient.employees[2].fullName || newData.Team_Member_4;
              newData.Team_Member_Role_4 = currentClient.employees[2].role || newData.Team_Member_Role_4;
              newData.Team_Member_4_LinkedIn = currentClient.employees[2].linkedin || newData.Team_Member_4_LinkedIn;
            }
          }

          return newData;
        });

        const grantDbProposalName = grantDbFormData.Grant_Proposal_Name;
        if (grantDbProposalName && !proposalNameFromNavigation) {
          setProfileName(grantDbProposalName);
        }
      }
    }, []);

        function changeStep(num) {
    if (num === 4) {
      setHasConfirmedGrantInfo(true);
    }
    // CHAT DELAY: Tab navigation also queues its instruction once, without
    // duplicating messages already shown after a confirmation.
    queueChatStepMessage(num);
    setCurrentStep(num);
  }

  const handleNext = () => {
    const requiredInputs = document.querySelectorAll("input[required]");
    const isEmpty = Array.from(requiredInputs).some(
      (input) => !input.value.trim()
    );
    if (isEmpty) {
      alert("Please fill in the required fields. (*)");
      return;
    }
    if (currentStep === 3) {
      setHasConfirmedGrantInfo(true);
    }
    setConfirmedChatSteps((confirmedSteps) =>
      confirmedSteps.includes(currentStep)
        ? confirmedSteps
        : [...confirmedSteps, currentStep]
    );
        const nextStep = currentStep + 1;
    // CHAT DELAY: Queue the next form's contextual guidance before moving on.
    queueChatStepMessage(nextStep);
    setCurrentStep(nextStep);
    console.log(currentStep);
  };

  const whiteBorderDatePickerStyles = {
    "& .MuiButtonBase-root": {
      color: "#D3D3D3",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "1.5px solid #19039E",
      },
      "&:hover fieldset": {
        borderColor: "#D3D3D3",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#D3D3D3",
      },
    },
  };

  const renderstepFields = (step) => {
    switch (step) {
      case 1:
        return (
          <div>
            <table>
              <tbody>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Applicant's Name *
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Applicant's Name *"
                      id="User_name"
                      value={formData.User_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Company Name *</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Company Name *"
                      id="Company_Name"
                      value={formData.Company_Name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Role *</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Role *"
                      id="User_role"
                      value={formData.User_role}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Company Website URL
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Company Website URL"
                      id="Company_Website"
                      value={formData.Company_Website}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Mission Statement
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Mission Statement"
                      id="Mission_Statement"
                      value={formData.Mission_Statement}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Vision Statement
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Vision Statement"
                      id="Vision_Statement"
                      value={formData.Vision_Statement}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Users/target Market *
                  </p>
                  <div className="form-item-styles">
                    {/* required */}
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Users/target Market *"
                      id="Target_Users_Market"
                      value={formData.Target_Users_Market}
                      onChange={handleChange}
                    />
                  </div>
                </td>
           <td style={{ maxWidth: '800px', width: '600px' }}>
        <p className="ml-0 text-[#b0afaf] text-xs">Industry *</p>
 <IndustryMultiSelect
  value={formData.Focused_Industry}
  onChange={(updatedIndustries) =>
    setFormData((prevData) => ({
      ...prevData,
      Focused_Industry: updatedIndustries,
    }))
  }
 />

      </td>

              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Problem Statement *
                  </p>
                  <div className="form-item-styles">
                    {/* required */}
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Problem Statement *"
                      id="Problem_Statement"
                      value={formData.Problem_Statement}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Solution Statement *
                  </p>
                  <div className="form-item-styles">
                    {/* required */}
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Solution Statement *"
                      id="Solution_Statement"
                      value={formData.Solution_Statement}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Programs, Services, and Product Features *
                  </p>
                  <div className="form-item-styles">
                    {/* required */}
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Programs, Services, and Features *"
                      id="Programs_Services"
                      value={formData.Programs_Services}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Track record and past success
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Track record and past success"
                      id="Track_record"
                      value={formData.Track_record}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Current funding sources
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Current funding sources"
                      id="Current_funding"
                      value={formData.Current_funding}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Budget</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Budget"
                      id="Budget"
                      value={formData.Budget}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr></tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Goals, Objectives, and Outcomes
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Goals, Objectives, and Outcomes"
                      id="Goals_Objectives_Outcomes"
                      value={formData.Goals_Objectives_Outcomes}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Long-term sustainability plan
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Long-term sustainability plan"
                      id="Long_Term_Plan"
                      value={formData.Long_Term_Plan}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Relevant Partnerships and Collaborators 1
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Relevant Partnerships and Collaborators 1"
                      id="Partners_Collaborators_1"
                      value={formData.Partners_Collaborators_1}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Relevant Partnerships and Collaborators 2
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Relevant Partnerships and Collaborators 2"
                      id="Partners_Collaborators_2"
                      value={formData.Partners_Collaborators_2}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        );
      case 2:
        return (
          <div>
            <table>
              <tbody>
              <tr>
                <td>
                  {/* UPDATED: 20px label */}
                  <p className="ml-0 text-[#b0afaf] font-bold text-[20px]">
                    Team Member 1 Name *
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Team Member 1 Name *"
                      id="Team_Member_1"
                      value={formData.Team_Member_1}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </td>
                <td>
                  {/* UPDATED: 20px label (this column is Team Member 3 in your markup) */}
                  <p className="ml-0 text-[#b0afaf] font-bold text-[20px]">
                    Team Member 3 Name
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Team Member 3 Name"
                      id="Team_Member_3"
                      value={formData.Team_Member_3}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Role *</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Role *"
                      id="Team_Member_Role_1"
                      value={formData.Team_Member_Role_1}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Role</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Role"
                      id="Team_Member_Role_3"
                      value={formData.Team_Member_Role_3}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Education</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Education"
                      id="Team_Member_Education_1"
                      value={formData.Team_Member_Education_1}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Education</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Education"
                      id="Team_Member_Education_3"
                      value={formData.Team_Member_Education_3}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">LinkedIn URL</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="LinkedIn URL"
                      id="Team_Member_1_LinkedIn"
                      value={formData.Team_Member_1_LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">LinkedIn URL</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="LinkedIn URL"
                      id="Team_Member_3_LinkedIn"
                      value={formData.Team_Member_3_LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Super Power/Skills
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Super Power/Skills"
                      id="Team_Member_Super_Power_Skills_1"
                      value={formData.Team_Member_Super_Power_Skills_1}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Super Power/Skills
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Super Power/Skills"
                      id="Team_Member_Super_Power_Skills_3"
                      value={formData.Team_Member_Super_Power_Skills_3}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr></tr>
              </tbody>
            </table>
            <table>
              <tbody>
              <tr>
                <td>
                  <label
                    type="text"
                    placeholder="_"
                    id="_"
                    onChange={handleChange}
                  />
                </td>
                <td>
                  <label
                    type="text"
                    placeholder="_"
                    id="_"
                    onChange={handleChange}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  {/* UPDATED: 20px label */}
                  <p className="ml-0 text-[#b0afaf] font-bold text-[20px]">
                    Team Member 2 Name
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Team Member 2 Name"
                      id="Team_Member_2"
                      value={formData.Team_Member_2}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  {/* UPDATED: 20px label */}
                  <p className="ml-0 text-[#b0afaf] font-bold text-[20px]">
                    Team Member 4 Name
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Team Member 4 Name"
                      id="Team_Member_4"
                      value={formData.Team_Member_4}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Role</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Role"
                      id="Team_Member_Role_2"
                      value={formData.Team_Member_Role_2}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Role</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Role"
                      id="Team_Member_Role_4"
                      value={formData.Team_Member_Role_4}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Education</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Education"
                      id="Team_Member_Education_2"
                      value={formData.Team_Member_Education_2}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Education</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Education"
                      id="Team_Member_Education_4"
                      value={formData.Team_Member_Education_4}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">LinkedIn URL</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="LinkedIn URL"
                      id="Team_Member_2_LinkedIn"
                      value={formData.Team_Member_2_LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">LinkedIn URL</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="LinkedIn URL"
                      id="Team_Member_4_LinkedIn"
                      value={formData.Team_Member_4_LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Super Power/Skills
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Super Power/Skills"
                      id="Team_Member_Super_Power_Skills_2"
                      value={formData.Team_Member_Super_Power_Skills_2}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Super Power/Skills
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Super Power/Skills"
                      id="Team_Member_Super_Power_Skills_4"
                      value={formData.Team_Member_Super_Power_Skills_4}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr></tr>
              </tbody>
            </table>
          </div>
        );
      case 4:
        return (
          <FileUploaderDropzone
            uid={uid}
            grantProposalFormId={grantProposalForm_id}
            clientOrganizationId={client_Organization_id}
            onParsedData={handleChildData}
            showExistingFiles
          />
        );
                        /* Previous inline upload UI moved to FileUploaderDropzone.
            case 5:
              return (
          <div className="flex flex-col items-center py-8 px-4">
            {null}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
              onDragLeave={() => setIsDraggingOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDraggingOver(false);
                const dropped = Array.from(e.dataTransfer.files);
                setUploadedFiles(prev => [...prev, ...dropped]);
              }}
              className={`border-2 border-dashed rounded-xl p-12 w-full max-w-2xl flex flex-col items-center gap-4 transition-all cursor-pointer ${
                isDraggingOver
                  ? 'border-purple-400 bg-purple-900/20'
                  : 'border-purple-600/50 bg-[#1a0052]/30 hover:border-purple-500 hover:bg-[#1a0052]/50'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <p style={{ fontFamily: 'Roboto, sans-serif', fontSize: '20px' }} className="text-white text-center">
                Drag &amp; drop files here
              </p>
              <p className="text-gray-400 text-sm">or</p>
              <label
                htmlFor="file-upload"
                style={{ fontFamily: 'Roboto, sans-serif', fontSize: '18px', cursor: 'pointer' }}
                className="bg-[#5e36a3] hover:bg-[#6d60eb] text-white px-8 py-2 rounded-lg transition-colors select-none"
              >
                Browse Files
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const selected = Array.from(e.target.files);
                    setUploadedFiles(prev => [...prev, ...selected]);
                    e.target.value = '';
                  }}
                />
              </label>
              <p className="text-gray-500 text-xs mt-2">Supports: PDF, DOC, DOCX, TXT, XLS, CSV</p>
            </div>

            {null}
            {uploadedFiles.length > 0 && (
              <div className="mt-6 w-full max-w-2xl">
                <p style={{ fontFamily: 'Roboto, sans-serif' }} className="text-gray-300 text-sm mb-3">
                  {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} selected:
                </p>
                <ul className="space-y-2">
                  {uploadedFiles.map((file, i) => (
                    <li key={i} className="flex items-center justify-between bg-[#1a0052]/60 border border-purple-800/40 rounded-lg px-4 py-2">
                      <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }} className="text-white truncate max-w-[80%]">
                        {file.name}
                      </span>
                      <button
                        onClick={() => setUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-red-400 transition-colors ml-3 text-lg leading-none"
                        title="Remove"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
                        */
            case 3:
              return (
          <div>
            <table>
            <tbody>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Grant Name *</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Name *"
                      id="Grant_Name"       
                      value={formData.Grant_Name} 
                      onChange={handleChange} 
                      required
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company Name *
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company Name *"
                      id="Grant_Company_Name"
                      value={formData.Grant_Company_Name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p
                    style={{
                      textAlign: "left",
                      color: "#b0aeae",
                      fontSize: "12px",
                      margin: "0px",
                      paddingLeft: "0px",
                    }}
                  >
                    Due Time *
                  </p>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box>
                      <MobileTimePicker
                        value={
                          formData.Due_Time
                            ? dayjs(formData.Due_Time, "HH:mm")
                            : null
                        }
                        onChange={(newValue) => {
                          if (newValue && newValue.isValid()) {
                            const formattedTime = newValue.format("HH:mm");
                            setFormData((prevData) => ({
                              ...prevData,
                              Due_Time: formattedTime,
                            }));
                          } else {
                            setFormData((prevData) => ({
                              ...prevData,
                              Due_Time: null,
                            }));
                          }
                        }}
                        slots={{
                          textField: (params) => (
                            <TextField
                              {...params}
                              required
                              sx={{
                                width: "165px",
                                backgroundColor: "#11002e",
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "#1a039e",
                                    borderWidth: "1.5px",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#d4d4d4",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#d4d4d4",
                                  },
                                },
                                "& input": {
                                  color: "white",
                                },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                sx: {
                                  height: "33px",
                                  padding: "9px",
                                  "& .MuiSvgIcon-root": { color: "#d4d4d4" },
                                },
                              }}
                            />
                          ),
                        }}
                        PopperProps={{ sx: { overflow: "hidden" } }}
                      />
                    </Box>
                  </LocalizationProvider>
                </td>

                <td>
                  <p
                    style={{
                      textAlign: "left",
                      color: "#b0aeae",
                      fontSize: "12px",
                    }}
                  >
                    Time Zone *
                  </p>
                 <CityTimeZoneSelector
                    value={formData.Due_Time_Zone}
                    onChange={(timezone) => {
                      setFormData((prevData) => ({
                        ...prevData,           
                        Due_Time_Zone: timezone,
                      }));
                    }}
                  />
                </td>
              </tr>

              <tr>
                <td colSpan={1}>
                  <p
                    style={{
                      textAlign: "left",
                      color: "#b0aeae",
                      fontSize: "12px",
                    }}
                  >
                    Due Date *
                  </p>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        formData.Due_Date ? dayjs(formData.Due_Date) : null
                      }
                      onChange={(newDate) => {
                        if (newDate && newDate.isValid()) {
                          setFormData((prevData) => ({
                            ...prevData,
                            Due_Date: newDate.format("YYYY-MM-DD"),
                          }));
                        } else {
                          setFormData((prevData) => ({
                            ...prevData,
                            Due_Date: null,
                          }));
                        }
                      }}
                      showDaysOutsideCurrentMonth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          InputLabelProps={{ style: { color: "white" } }}
                        />
                      )}
                      required
                      sx={whiteBorderDatePickerStyles}
                    />
                  </LocalizationProvider>
                </td>
              </tr>

              <td>
                <p className="ml-0 text-[#b0afaf] text-xs">Grant Amount *</p>
                <div className="form-item-styles">
                  <input
                    className="transparent-placeholder"
                    type="number"
                    placeholder="Grant Amount"
                    id="Grant_Amount"
                    value={formData.Grant_Amount}
                    onFocus={(e) => {
                      if (formData.Grant_Amount === 0) {
                        e.target.value = "";
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === "") {
                        setFormData((prevData) => ({
                          ...prevData,
                          Grant_Amount: 0,
                        }));
                      }
                    }}
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      setFormData((prevData) => ({
                        ...prevData,
                        Grant_Amount: isNaN(newValue) ? 0 : newValue,
                      }));
                    }}
                    required
                  />
                </div>
              </td>
              <td>
                <p className="ml-0 text-[#b0afaf] text-xs">
                  Grant Application Webpage URL
                </p>
                <div className="form-item-styles">
                  <input
                    className="transparent-placeholder"
                    type="text"
                    placeholder="Grant Application Webpage URL"
                    id="Grant_Application_Website"
                    value={formData.Grant_Application_Website}
                    onChange={handleChange}
                  />
                </div>
              </td>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company Category / Industry
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company Category / Industry"
                      id="Grant_Company_Category_Industry"
                      value={formData.Grant_Company_Category_Industry}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company Target Market
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company Target Market"
                      id="Grant_Company_Target_Market"
                      value={formData.Grant_Company_Target_Market}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Agency Type (e.g. Federal, State, Local, Organization)
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Agency Type  (e.g. Federal, State, Local, Organization)"
                      id="Agency_Type"
                      value={formData.Agency_Type}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Geographic Focus (US state)
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Geographic Focus (US state)"
                      id="Geographic_Focus"
                      value={formData.Geographic_Focus}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company Mission Statement
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company Mission Statement"
                      id="Grant_Company_Mission_Statement"
                      value={formData.Grant_Company_Mission_Statement}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company Vision Statement
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company Vision Statement"
                      id="Grant_Company_Vision_Statement"
                      value={formData.Grant_Company_Vision_Statement}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company LinkedIn URL
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company LinkedIn URL"
                      id="Grant_Company_LinkedIn"
                      value={formData.Grant_Company_LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Grant Company Website URL
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Grant Company Website URL"
                      id="Grant_Company_Website"
                      value={formData.Grant_Company_Website}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <label type="text" id="_" onChange={handleChange} />
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Judge's Name</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Judge's Name"
                      id="Judges_Name"
                      value={formData.Judges_Name}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">Role</p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Role"
                      id="Judges_Role"
                      value={formData.Judges_Role}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    Judge's Linkedin URL
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="Judge's Linkedin URL"
                      id="Judges_LinkedIn"
                      value={formData.Judges_LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                </td>
                <td>
                  <p className="ml-0 text-[#b0afaf] text-xs">
                    More Relevant info about Grant Admin/Judge
                  </p>
                  <div className="form-item-styles">
                    <input
                      className="transparent-placeholder"
                      type="text"
                      placeholder="More Relevant info about Grant Admin/Judge"
                      id="Judges_More_Relevant_info"
                      value={formData.Judges_More_Relevant_info}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        );
    }
  };

  // Match the exact condition used by the Eligibility chat before it offers to
  // start a proposal. Until then, the final save action must remain unavailable.
  const canSaveAndStart = Boolean(
    eligibilityResult &&
    !eligibilityLoading &&
    eligibilityResult.eligible === true
  );

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSaveAndStart) return;

     // 1) Check for required inputs exactly as before
    const requiredInputs = document.querySelectorAll("input[required]");
    const isEmpty = Array.from(requiredInputs).some(
      (input) => !input.value.trim()
    );
    const grantProposalName = formData.Grant_Proposal_Name.trim();
    console.log(isEmpty, formData);
    if (isEmpty) {
      alert("Please fill in the required fields. (*)");
      return;
    }
    if (!grantProposalName) {
      alert("Please enter a Grant Proposal Name before saving.");
      return;
    }
    const formDataToSave = {
      ...formData,
      Grant_Proposal_Name: grantProposalName,
    };
     // 2) Grab currentProposal and currentClient from location.state, if any

    const currentProposal = location?.state?.currentProposal;
    const currentClient =
      location?.state?.currentClient || client_Organization_id;
     // 3) Generate a new collaborator ID as you did before
    const userCollaboratorId = generateFirestoreAutoId();
     // 4) Build the payload, ALWAYS using formData.Grant_Proposal_Name
    const formPayload = {
      form: formDataToSave,

      user_collaborator_id: userCollaboratorId,
      grantProposalForm_id: grantProposalForm_id,
      client_Organization_id:
        currentClient?.client_id || client_Organization_id,
      profile: grantProposalName,
      user: uid,
      Company_Name: formData.Company_Name,
      Grant_Proposal_Name: grantProposalName,

    };
    console.log("Payload about to send:", formPayload);
     // 5) Decide whether to actually POST or pop the “name already exists” dialog
    let shouldSubmit = false;
     if (!currentProposal) {
      const firstClient = await addCollaborator("User_Collaborator");
      formPayload.user_collaborator_id =
        firstClient?.collaborator_id || userCollaboratorId;
      shouldSubmit = true;
     } else if (currentProposal.id !== currentClient?.client_id) {
      if (
          currentProposal.Grant_Proposal_Name !==
          grantProposalName ||

        typeof currentProposal.Grant_Proposal_Name === "undefined"
      ) {
        setOverwriteConfirmed(false);
        shouldSubmit = true;
      } else {
        setOverwriteConfirmed(true);
      }
     } else {
      shouldSubmit = true;
    }
     if (shouldSubmit) {
      try {
        const response = await fetch(`${baseurl}/form`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formPayload),
        });
                  if (response.ok) {
          const clientID = currentClient?.client_id || client_Organization_id;
          navigate(
            `/grant-assistant/${clientID}/${grantProposalForm_id}`,
            {
              state: {
                from: "grantform",
                                grantProposalForm_id,
                clientID,
                profileName: grantProposalName,
                formData: formDataToSave,
                                // GENIE HANDOFF: Pass only the variable eligibility snapshot.
                // Main reconstructs its fixed Genie guidance from shared constants.
                grantProfileChat: (() => {
                  const eligibilitySnapshot = createEligibilityChatSnapshot({
                    eligibilityResult,
                    eligibilityError,
                    clarifications,
                    proposalPromptAnswer,
                    grantUrl: location.state?.grantUrl || "",
                    hasConfirmedGrantInfo,
                  });

                  // LOCAL FALLBACK: Retain this proposal's snapshot after reloads
                  // and direct visits in this browser. No Firebase/backend write.
                  saveEligibilityChatSnapshot(
                    {
                      uid,
                      // STORAGE IDENTIFIER: The local variable is named clientID;
                      // map it to the helper's expected clientId property.
                      clientId: clientID,
                      grantId: grantProposalForm_id,
                    },
                    eligibilitySnapshot
                  );

                  return eligibilitySnapshot;
                })(),



              },
            }
          );
        } else {
          console.log("Error updating the data");
        }
      } catch (error) {
        console.error("Network error:", error);
      }
    }
  }
  //getting the data from the file upload component then setting the state with the data
  function handleChildData(data) {
    console.log("childData:", data);
    if (data.source === "modal") {
        setFormData(prev => ({
          ...prev,
          Company_Name: data.Company_Name,
          Grant_Proposal_Name: data.profileName
        }));
        setProfileName(data.profileName);
      } else if (data.source === "file") {
        setFormData(prev => {
          const next = { ...prev };
          const forbidden = new Set([
            "source",
            "Company_Name",
            "profileName",
            "Grant_Proposal_Name",
            "Grant_Company_Name",
            "Grant_Name"
          ]);
   
          for (const [key, value] of Object.entries(data)) {
            if (forbidden.has(key))   continue;
            if (value == null)         continue;
            if (prev[key])             continue;
            next[key] = value;
          }
          return next;
        });
      }
    }

  return (
    <div className="App">
      <Modal
        open={isApplicationQuestionsOpen}
        onClose={closeApplicationQuestions}
        aria-labelledby="application-questions-title"
      >
        <Box className="application-questions-modal">
          <div className="application-questions-heading">
            <h2 id="application-questions-title">No Application found</h2>
            <p>Please enter Application Question</p>
          </div>

          <div className="application-questions-list">
            {applicationQuestions.map((question, index) => (
              <div className="application-question-row" key={index}>
                <label htmlFor={`application-question-${index}`}>
                  {index + 1}.
                </label>
                <div className="application-question-input">
                  <textarea
                    id={`application-question-${index}`}
                    value={question}
                    onChange={(event) =>
                      updateApplicationQuestion(index, event.target.value)
                    }
                    rows={3}
                    placeholder="Type your question here..."
                    aria-label={`Application question ${index + 1}`}
                  />
                  <div className="application-question-field-actions">
                    <button
                      type="button"
                      className="application-question-undo"
                      onClick={() => undoClearApplicationQuestion(index)}
                      disabled={applicationQuestionUndo[index] === undefined}
                      aria-label={`Undo clearing application question ${index + 1}`}
                      title="Undo clear"
                    >
                      <UndoIcon fontSize="small" />
                      <span>Undo</span>
                    </button>
                    <button
                      type="button"
                      className="application-question-clear"
                      onClick={() => clearApplicationQuestion(index)}
                      disabled={!question}
                      aria-label={`Clear application question ${index + 1}`}
                      title="Clear question"
                    >
                      <ClearIcon fontSize="small" />
                      <span>Clear</span>
                    </button>
                    <button
                      type="button"
                      className="application-question-delete"
                      onClick={() => deleteApplicationQuestion(index)}
                      disabled={applicationQuestions.length === 1}
                      aria-label={`Delete application question ${index + 1}`}
                      title={
                        applicationQuestions.length === 1
                          ? "At least one question is required"
                          : "Delete question"
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="application-question-add"
            onClick={addApplicationQuestion}
          >
            <span aria-hidden="true">+</span> Add Question
          </button>

          <div className="application-question-actions">
            {applicationQuestionsError && (
              <p className="application-questions-error" role="alert">
                {applicationQuestionsError}
              </p>
            )}
            <button
              type="button"
              className="application-question-cancel"
              onClick={closeApplicationQuestions}
            >
              Cancel
            </button>
            <button
              type="button"
              className="application-question-submit"
              onClick={submitApplicationQuestions}
            >
              Submit
            </button>
          </div>
        </Box>
      </Modal>

      {overwriteConfirmed && (
        <dialog
          open
          className="space-y-2 bg-[#1f1f1f] text-[#dedede] p-10 w-[528px] h-[200px] z-10 rounded-xl"
        >
          {/* <h3 className="text-2xl font-bold">www.grantaide.com</h3> */}
          <h3 className="text-2xl font-bold">http://localhost:3000</h3>
          <p className="">Proposal name already exists. Rename proposal.</p>
          <form method="dialog" className="flex justify-end px-20">
            <button
              className="mt-4 text-[#9eb5de] border border-[#306791] rounded-3xl py-2 px-4 "
              onClick={() => setOverwriteConfirmed(false)}
            >
              Cancel
            </button>
          </form>
        </dialog>
      )}
      <NavBar />

      <div className="page-wrapper">
        {/* LEFT PANEL: Eligibility Agent Chat (584px width) */}

                {/* Shared chat panel receives the existing page state and callbacks unchanged. */}
        <GeninieChatPanel
          activeChatTab={activeChatTab}
          onActiveChatTabChange={setActiveChatTab}
          currentStep={currentStep}
          hasConfirmedGrantInfo={hasConfirmedGrantInfo}
          grantUrl={location.state?.grantUrl}
          eligibilityLoading={eligibilityLoading}
          eligibilityError={eligibilityError}
          eligibilityResult={eligibilityResult}
          clarifications={clarifications}
          proposalPromptAnswer={proposalPromptAnswer}
                    onAnswerUncertainty={handleAnswerUncertainty}
          onProposalPromptAction={handleProposalPromptAction}
          // CHAT CONTINUITY: Includes prior-page guidance and delayed form prompts.
          chatMessages={chatMessages}
          isTyping={isProfileChatTyping}
          onConfirmStep={handleNext}
          confirmedChatSteps={confirmedChatSteps}
          showSearchButton
        />

        {/* Legacy inline panel retained temporarily as non-rendered reference during extraction. */}
        {false && (
        <aside className="chat-panel">
        {/* <aside className="chat-panel"
        style={{ borderRight: 'none', boxShadow: 'none' }}
        > */}
          {/* NEW: Title added here */}
          {/* <div style={{ marginTop: '20px', marginBottom: '10px', paddingLeft: '16px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
              Eligibility
            </h2>
          </div> */}
          <div style={{ 
            marginTop: '18px', 
            marginBottom: '16px', 
            paddingLeft: '16px',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '22px',
            fontWeight: 'bold',
            color: 'white'
          }}>
            Eligibility
          </div>

          {/* Chat Tabs: Copilot / Content */}
          <div className="chat-tabs">
            <button
              className={`chat-tab-btn ${activeChatTab === 'eligibility' ? 'active' : ''}`}
              onClick={() => setActiveChatTab('eligibility')}
            >
              {/* Eligibility Agent (changed name from eligibility agent to Genie*/} 
              Genie
            </button>
            <button
              className={`chat-tab-btn ${activeChatTab === 'copilot' ? 'active' : ''}`}
              onClick={() => setActiveChatTab('copilot')}
            >
              Copilot
            </button>
          </div>

                    <div className="chat-container custom-scrollbar">
                      {activeChatTab === 'eligibility' ? (
                        <>
                          <div className="px-4 space-y-4 mt-4 mb-4">
                            {currentStep >= 1 && (
                              <div className="flex justify-start">
                                <div className="bg-[#412293] px-4 py-3 rounded-2xl rounded-tl-none text-[15px] leading-relaxed text-white max-w-[90%] shadow-md">
                                  Please review and confirm your Company Info on the right, then click Confirm.
                                </div>
                              </div>
                            )}
                            {currentStep >= 2 && (
                              <div className="flex justify-start">
                                <div className="bg-[#412293] px-4 py-3 rounded-2xl rounded-tl-none text-[15px] leading-relaxed text-white max-w-[90%] shadow-md">
                                  Please review and confirm your Team Info on the right, then click Confirm.
                                </div>
                              </div>
                            )}
                            {currentStep >= 3 && (
                              <div className="flex justify-start">
                                <div className="bg-[#412293] px-4 py-3 rounded-2xl rounded-tl-none text-[15px] leading-relaxed text-white max-w-[90%] shadow-md">
                                  Please review and confirm the Grant Info on the right, then click Confirm.
                                </div>
                              </div>
                            )}
                          </div>

                          {hasConfirmedGrantInfo && (
                            <>
                              {/* Checking URL Bar */}
                <div className="checking-url-bar">
                  <span className="font-bold">Checking URL: </span>
                  {location.state?.grantUrl || "No URL provided"}
                </div>

                {/* Eligibility Result Card */}
                {eligibilityLoading && (
                  <div className="px-4 py-2 text-gray-400 italic">Analyzing eligibility...</div>
                )}

                {eligibilityError && (
                  <div className="mx-4 mb-4 p-3 bg-red-900/40 text-red-200 text-xs rounded border border-red-800">
                    {eligibilityError}
                  </div>
                )}

                {eligibilityResult && (
                  <div className="eligibility-card shadow-lg">
                    <p className="font-bold text-lg mb-2 text-center border-b border-white/10 pb-2">
                      Eligibility: {eligibilityResult.eligible === true ? "Eligible" : eligibilityResult.eligible === false ? "Not Eligible" : "Uncertain"}
                    </p>
                    <p className="font-bold text-sm mb-4 text-center">
                      AI Certainty: <span className={
                        eligibilityResult.confidence === 'High' ? 'text-green-400' :
                          eligibilityResult.confidence === 'Medium' ? 'text-yellow-400' :
                            'text-red-400'
                      }>{eligibilityResult.confidence}</span>
                    </p>

                    {eligibilityResult.reasons?.length > 0 && (
                      <div className="mb-4">
                        <p className="font-semibold text-xs uppercase text-purple-300 mb-1">Breakdown:</p>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          {eligibilityResult.reasons.map((r, idx) => <li key={idx}>{r}</li>)}
                        </ul>
                      </div>
                    )}

                    {/* Source Pages */}
                    {eligibilityResult.source_pages?.length > 0 && (
                      <div>
                        <p className="font-semibold text-xs uppercase text-purple-300 mb-1">Source Pages Scraped:</p>
                        <ul className="list-disc pl-5 text-[10px] text-blue-300 space-y-1">
                          {eligibilityResult.source_pages.map((page, idx) => (
                            <li key={idx} className="break-all italic">
                              <a href={page} target="_blank" rel="noreferrer" className="hover:underline">{page}</a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                                {/* Interaction Flow: Uncertainties -> Proposal Prompt */}
                <div className="px-4 space-y-4">
                  {/* Uncertainties Chat History */}
                  {Object.entries(clarifications).map(([q, a], idx) => (
                    <div key={idx} className="flex flex-col gap-2">
                      <div className="flex justify-start">
                        <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-sm text-white max-w-[90%]">
                          {q}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="bg-[#8749E7] px-4 py-1 rounded-full rounded-tr-none text-xs text-white capitalize">
                          {a}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pending Uncertainty (Follow up question) */}
                  {(() => {
                    if (!eligibilityResult?.uncertainties || eligibilityLoading) return null;
                    const pendingUncertainties = eligibilityResult.uncertainties.filter(u => {
                      const qText = typeof u === 'string' ? u : u.question;
                      return !clarifications[qText];
                    });
                    
                    if (pendingUncertainties.length > 0) {
                      const u = pendingUncertainties[0];
                      const questionText = typeof u === 'string' ? u : u.question;
                      return (
                        <div className="flex flex-col gap-2">
                          <div className="flex justify-start">
                            <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-sm text-white max-w-[90%]">
                              {questionText}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleAnswerUncertainty(questionText, true)} className="bg-[#5406D2] px-4 py-1 rounded-full text-xs text-white hover:bg-[#6d60eb]">Yes</button>
                            <button onClick={() => handleAnswerUncertainty(questionText, false)} className="bg-white/10 px-4 py-1 rounded-full text-xs text-white hover:bg-white/20">No</button>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                                    {/* Proposal Prompt */}
                  {eligibilityResult && !eligibilityLoading && eligibilityResult.eligible === true && (
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-start">
                        <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-sm text-white">
                          Want to start the grant proposal?
                        </div>
                      </div>
                      {proposalPromptAnswer === null ? (
                        <div className="flex gap-2">
                          <button onClick={() => handleProposalPromptAction("yes")} className="bg-[#5406D2] px-4 py-1 rounded-full text-xs text-white hover:bg-[#6d60eb]">Yes</button>
                          <button onClick={() => handleProposalPromptAction("no")} className="bg-white/10 px-4 py-1 rounded-full text-xs text-white hover:bg-white/20">No</button>
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <div className="bg-[#8749E7] px-4 py-1 rounded-full rounded-tr-none text-xs text-white capitalize">
                            {proposalPromptAnswer}
                          </div>
                        </div>
                      )}
                                            {proposalPromptAnswer === "yes" && (
                        <div className="flex justify-start">
                          <div className="bg-[#412293] px-4 py-2 rounded-2xl rounded-tl-none text-sm text-white">
                            Starting proposal...
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
              )}
            </>
            ) : (
              /* CONTENT TAB: Placeholder for table of content / snippets */
              <div className="p-8 text-center text-gray-500">
                <p className="text-sm">Copilot features Should be moved here</p>
              </div>
            )}
          </div>

                    {/* Bottom: Re-run Eligibility Check */}
          <div className="chat-input-area">
            {hasConfirmedGrantInfo && (
              <button
                onClick={() => handleEligibilityCheck()}
                disabled={eligibilityLoading}
                style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}
                className={`w-full py-2 px-4 rounded-md border border-white/20 text-white transition-all
                  ${eligibilityLoading
                    ? 'bg-purple-900/50 cursor-not-allowed opacity-50'
                    : 'bg-[#492A7B] hover:bg-[#5a3a8b] cursor-pointer'}`}
              >
                {eligibilityLoading ? "Analyzing…" : "Re-run Eligibility Check"}
              </button>
            )}
          </div>
                </aside>
        )}

        {/* RIGHT PANEL: Form (shifted 584px) */}
        <main className="form-panel">
          {/* Tab Bar: Shifted and center-aligned */}
          <div className="form-tabs-container">
            {[
              { step: 1, label: '1 Company Info' },
              { step: 2, label: '2 Team Info' },
              { step: 3, label: '3 Grant Info' },
              { step: 4, label: 'Upload Files' },
            ].map(({ step, label }) => (
              <button
                key={step}
                onClick={() => changeStep(step)}
                style={{
                  width: '222px',
                  height: '42px',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '20px',
                  fontWeight: currentStep === step ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  background: currentStep === step
                    ? 'linear-gradient(135deg, #8749E7 0%, #5406D2 100%)'
                    : '#1a0052',
                  color: currentStep === step ? '#ffffff' : '#9d8cbe',
                  boxShadow: currentStep === step
                    ? '0 2px 12px rgba(84,6,210,0.5)'
                    : '0 1px 4px rgba(0,0,0,0.4)',
                  outline: currentStep === step
                    ? '1.5px solid rgba(255,255,255,0.25)'
                    : '1.5px solid rgba(84,6,210,0.35)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="form">
            <div className="form-body">
              <form
                key={currentStep}
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1em",
                }}
              >
                {renderstepFields(currentStep)}
              </form>
            </div>
                        <div className="form-footer">
              {currentStep < totalSteps ? (
                <div className="button" onClick={handleNext}>
                  <label type="button" style={{ cursor: "pointer" }}> Confirm </label>
                </div>
              ) : (
                <div
                  className={`button ${canSaveAndStart ? "" : "button-disabled"}`}
                  onClick={canSaveAndStart ? handleSubmit : undefined}
                  aria-disabled={!canSaveAndStart}
                >
                  <label
                    type="button"
                    style={{ cursor: canSaveAndStart ? "pointer" : "not-allowed" }}
                  >
                    Save and Start
                  </label>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Main;
